import { KeychainAccess, SecureStorage, type SecureStoragePlugin } from '@aparajita/capacitor-secure-storage'

import { isNativePlatform } from './platform'
import { type NativeAuthTokens, parseNativeAuthTokens } from './tokens'

const SESSION_TOKENS_KEY = 'session.tokens'
const SESSION_KEYCHAIN_ACCESS = KeychainAccess.whenUnlockedThisDeviceOnly

type SecureStorageApi = Pick<
  SecureStoragePlugin,
  'get' | 'remove' | 'set' | 'setDefaultKeychainAccess' | 'setKeyPrefix' | 'setSynchronize'
>

interface NativeSessionDependencies {
  readonly native?: boolean
  readonly secureStorage?: SecureStorageApi
}

const getStorage = (secureStorage: SecureStorageApi = SecureStorage): SecureStorageApi => secureStorage

const configureStorage = async (storage: SecureStorageApi): Promise<void> => {
  await storage.setSynchronize(false)
  await storage.setKeyPrefix('aroya_')
  await storage.setDefaultKeychainAccess(SESSION_KEYCHAIN_ACCESS)
}

const requireNative = (native: boolean): void => {
  if (!native) throw new Error('Session storage is only available in the native AROYA app.')
}

export const readNativeSessionTokens = async ({
  native = isNativePlatform(),
  secureStorage,
}: NativeSessionDependencies = {}): Promise<NativeAuthTokens | null> => {
  requireNative(native)
  const storage = getStorage(secureStorage)
  await configureStorage(storage)
  const storedTokens = await storage.get(SESSION_TOKENS_KEY, false, false)
  if (storedTokens === null) return null
  const tokens = parseNativeAuthTokens(storedTokens)
  if (!tokens) throw new TypeError('Stored native session tokens are invalid.')
  return tokens
}

export const writeNativeSessionTokens = async (
  tokens: unknown,
  { native = isNativePlatform(), secureStorage }: NativeSessionDependencies = {},
): Promise<void> => {
  requireNative(native)
  const parsedTokens = parseNativeAuthTokens(tokens)
  if (!parsedTokens) throw new TypeError('Invalid native session tokens.')
  const storage = getStorage(secureStorage)
  await configureStorage(storage)
  await storage.set(SESSION_TOKENS_KEY, parsedTokens, false, false, SESSION_KEYCHAIN_ACCESS)
}

export const removeNativeSessionTokens = async ({
  native = isNativePlatform(),
  secureStorage,
}: NativeSessionDependencies = {}): Promise<void> => {
  requireNative(native)
  const storage = getStorage(secureStorage)
  await configureStorage(storage)
  await storage.remove(SESSION_TOKENS_KEY, false)
}
